/* =============================================================================
   VIEWS / ADMIN / RESERVATIONS-CUSTOMERS — Kundenprofile + Such-Modal + Historie
   =============================================================================
   S69 REF-3 Cut 2/5: aus reservations.js (1.429 LOC) extrahiert.

   Enthält:
     - buildCustomerProfiles()        — Aggregation aller Reservierungen je
                                        Kunde (Key = E-Mail || Telefon || Name)
     - openCustomerSearch()           — Such-Modal öffnen
     - renderCustomerSearchModal()    — Listen-Render im Such-Modal
     - openCustomerProfile(key)       — Detail-View eines Kundenprofils
     - getCustomerHistory(reservation)— Frühere Buchungen desselben Kunden
     - customerStatusBadge(history)   — VIP/Stammgast/Neu-Badge

   Cross-Module-Calls (via window):
     - closeModalThenOpenReservationDetail() lebt in core/setting-helpers.js,
       die Bridge ist bereits via window registriert.
============================================================================ */

import { htmlEscape, fmtDate, fmtDateShort, todayISO } from '../../utils/helpers.js';
import { getState, statusBadge } from './reservations-helpers.js';

export function buildCustomerProfiles() {
  const state = getState();
  const map = new Map();
  for (const r of (state.reservations || [])) {
    const email = (r.email || '').trim().toLowerCase();
    const phone = (r.phone || '').replace(/\D/g, '');
    const key = email || phone || r.name;
    if (!key) continue;
    if (!map.has(key)) {
      map.set(key, {
        key, name: r.name, email: r.email, phone: r.phone,
        reservations: [],
        firstVisit: r.date, lastVisit: r.date,
        confirmedCount: 0, noShowCount: 0, cancelledCount: 0,
        totalGuests: 0
      });
    }
    const p = map.get(key);
    p.reservations.push(r);
    if (r.date < p.firstVisit) p.firstVisit = r.date;
    if (r.date > p.lastVisit) p.lastVisit = r.date;
    if (r.status === 'confirmed' || r.status === 'completed') { p.confirmedCount++; p.totalGuests += r.guests; }
    if (r.status === 'no-show') p.noShowCount++;
    if (r.status === 'cancelled' || r.status === 'declined') p.cancelledCount++;
    if (r.createdAt && (!p._latestCreated || r.createdAt > p._latestCreated)) {
      p._latestCreated = r.createdAt;
      p.name = r.name;
    }
  }
  return Array.from(map.values()).sort((a, b) => b.lastVisit.localeCompare(a.lastVisit));
}

let _customerSearchQuery = '';

export function openCustomerSearch() {
  _customerSearchQuery = '';
  renderCustomerSearchModal();
}

export function renderCustomerSearchModal() {
  const profiles = buildCustomerProfiles();
  const q = _customerSearchQuery.trim().toLowerCase();
  const filtered = q
    ? profiles.filter(p =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.phone || '').toLowerCase().includes(q))
    : profiles;
  const total = profiles.length;

  function rank(p) {
    if (p.confirmedCount >= 5) return { label: 'VIP · Stammgast', cls: 'badge-confirmed' };
    if (p.confirmedCount >= 2) return { label: 'Wiederkehrend', cls: 'badge-confirmed' };
    if (p.confirmedCount === 1) return { label: 'Bekannt', cls: 'badge-pending' };
    return { label: 'Neu', cls: 'badge-pending' };
  }

  const html = `
    <div class="modal-header">
      <h2>Kundenprofile</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <p style="color: var(--ink-soft); font-size: .9rem;">
      ${total} Kund:innen insgesamt. Gruppiert nach E-Mail oder Telefon.
    </p>
    <input type="text" placeholder="Suche Name, E-Mail, Telefon..."
      value="${htmlEscape(_customerSearchQuery)}"
      oninput="_customerSearchQuery=this.value; renderCustomerSearchModal();"
      style="margin-bottom: 14px;" autofocus>
    <div class="customer-list">
      ${filtered.length === 0
        ? '<div class="empty"><div class="empty-emoji">🔍</div>Keine Kund:innen gefunden.</div>'
        : filtered.slice(0, 80).map(p => {
            const r = rank(p);
            return `
              <div class="customer-card" onclick="openCustomerProfile('${encodeURIComponent(p.key)}')">
                <div class="customer-card-head">
                  <strong>${htmlEscape(p.name)}</strong>
                  <span class="badge ${r.cls}">${r.label}</span>
                </div>
                <div class="customer-card-meta">
                  ${p.email ? '✉️ ' + htmlEscape(p.email) : ''}
                  ${p.phone ? '<span style="margin-left:8px;">☎ ' + htmlEscape(p.phone) + '</span>' : ''}
                </div>
                <div class="customer-card-stats">
                  ${p.reservations.length} Reservierungen ·
                  ${p.confirmedCount} bestätigt ·
                  ${p.noShowCount > 0 ? '<span style="color:var(--danger);">' + p.noShowCount + ' No-Show · </span>' : ''}
                  letzter Besuch: ${fmtDateShort(p.lastVisit)}
                </div>
              </div>
            `;
          }).join('')}
      ${filtered.length > 80 ? `<div class="empty" style="padding: 20px;">+ ${filtered.length - 80} weitere — bitte präziser suchen.</div>` : ''}
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
  document.getElementById('modal-overlay').classList.add('active');
}

export function openCustomerProfile(encodedKey) {
  const key = decodeURIComponent(encodedKey);
  const profile = buildCustomerProfiles().find(p => p.key === key);
  if (!profile) return;

  const sorted = profile.reservations.slice().sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
  const html = `
    <div class="modal-header">
      <h2>${htmlEscape(profile.name)}</h2>
      <button class="modal-close" onclick="closeModal()">×</button>
    </div>
    <button class="btn btn-ghost btn-sm" onclick="renderCustomerSearchModal()" style="margin-bottom: 12px;">← Zurück zur Liste</button>
    <div class="customer-card-meta" style="margin-bottom: 14px;">
      ${profile.email ? '<div>✉️ <a href="mailto:' + htmlEscape(profile.email) + '">' + htmlEscape(profile.email) + '</a></div>' : ''}
      ${profile.phone ? '<div>☎ <a href="tel:' + htmlEscape(profile.phone) + '">' + htmlEscape(profile.phone) + '</a></div>' : ''}
    </div>
    <div class="stats" style="margin-bottom: 14px;">
      <div class="stat" style="padding: 10px;">
        <div class="stat-label">Reservierungen</div>
        <div class="stat-value" style="font-size: 1.3rem;">${profile.reservations.length}</div>
      </div>
      <div class="stat" style="padding: 10px;">
        <div class="stat-label">Bestätigt</div>
        <div class="stat-value" style="font-size: 1.3rem; color: var(--green-dark);">${profile.confirmedCount}</div>
      </div>
      <div class="stat" style="padding: 10px;">
        <div class="stat-label">No-Show</div>
        <div class="stat-value" style="font-size: 1.3rem; color: ${profile.noShowCount>0?'var(--danger)':'var(--ink-muted)'};">${profile.noShowCount}</div>
      </div>
      <div class="stat" style="padding: 10px;">
        <div class="stat-label">Ø Gruppengröße</div>
        <div class="stat-value" style="font-size: 1.3rem;">${profile.confirmedCount > 0 ? (profile.totalGuests / profile.confirmedCount).toFixed(1) : '–'}</div>
      </div>
    </div>
    <h3 style="margin-bottom: 10px;">Alle Reservierungen</h3>
    <div class="customer-history">
      ${sorted.map(r => `
        <div class="history-row" onclick="closeModalThenOpenReservationDetail('${r.id}')" style="cursor: pointer;">
          <span class="history-date">${fmtDate(r.date)} · ${r.time} Uhr</span>
          <span class="history-guests">${r.guests}P</span>
          <span class="history-status">${statusBadge(r)}</span>
        </div>
      `).join('')}
    </div>
  `;
  document.getElementById('modal-content').innerHTML = html;
}

export function getCustomerHistory(reservation) {
  const state = getState();
  if (!reservation) return [];
  const myEmail = (reservation.email || '').trim().toLowerCase();
  const myPhone = (reservation.phone || '').replace(/\D/g, '');
  return (state.reservations || [])
    .filter(r => r.id !== reservation.id)
    .filter(r => {
      const e = (r.email || '').trim().toLowerCase();
      const p = (r.phone || '').replace(/\D/g, '');
      return (myEmail && e === myEmail) || (myPhone && p === myPhone);
    })
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time));
}

/**
 * S146 — Teilt die Kunden-Historie (Ergebnis von getCustomerHistory) in echte
 * Vergangenheit (`date < heute`) und kommende/parallele Buchungen
 * (`date >= heute`, also auch HEUTE). Behebt, dass heutige + zukünftige Buchungen
 * desselben Gasts fälschlich als „Vergangene Besuche" gelabelt wurden (z. B. bei
 * mehreren parallel angelegten Reservierungen für denselben Tag).
 * Pure + testbar — `today` ist injizierbar (Default todayISO()).
 */
export function splitCustomerHistory(history, today) {
  const t = today || todayISO();
  const past = [];
  const upcoming = [];
  for (const h of (history || [])) {
    if (!h) continue;
    if (h.date < t) past.push(h);
    else upcoming.push(h);
  }
  return { past, upcoming };
}

export function customerStatusBadge(history) {
  if (!history.length) return '<span class="badge badge-pending">Neuer Gast</span>';
  const confirmed = history.filter(r => r.status === 'confirmed' || r.status === 'completed').length;
  const noShows = history.filter(r => r.status === 'no-show').length;
  let badge = '';
  if (confirmed >= 5) badge += '<span class="badge badge-confirmed">VIP · Stammgast</span>';
  else if (confirmed >= 2) badge += '<span class="badge badge-confirmed">Wiederkehrender Gast</span>';
  else badge += '<span class="badge badge-pending">' + history.length + ' Vor-Besuch' + (history.length>1?'e':'') + '</span>';
  if (noShows > 0) badge += ' <span class="badge badge-declined">' + noShows + '× No-Show</span>';
  return badge;
}

// window-Bindings für inline-onclick-Konsumenten.
// _customerSearchQuery via getter/setter (oninput-Strings setzen direkt).
if (typeof window !== 'undefined') {
  Object.assign(window, {
    buildCustomerProfiles, openCustomerSearch, renderCustomerSearchModal,
    openCustomerProfile, getCustomerHistory, splitCustomerHistory, customerStatusBadge
  });
  Object.defineProperty(window, '_customerSearchQuery', {
    get() { return _customerSearchQuery; },
    set(v) { _customerSearchQuery = v; },
    configurable: true
  });
}
